<template>
  <div class="tracking-page">
    <tracking-page-header
      title="Weekly story for the business"
      subtitle="A short report you can share in meetings — what happened, what went wrong, and what to do next."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; loadData()"
      @refresh="loadData"
    >
      <template #actions>
        <button class="btn trk-btn-ghost" type="button" @click="exportCsv"><i class="fa fa-download"></i> Download data</button>
      </template>
    </tracking-page-header>

    <div class="trk-insight" :class="insight.tone">
      <div class="trk-insight-badge">W</div>
      <div>
        <h3>This week in one glance</h3>
        <ul>
          <li v-for="(item, idx) in displayHighlights" :key="idx">{{ item }}</li>
        </ul>
        <p class="trk-insight-action">Recommended focus: {{ insight.action }}</p>
      </div>
    </div>

    <div class="trk-kpi-grid">
      <div class="trk-kpi">
        <div class="trk-kpi-label">People who started</div>
        <div class="trk-kpi-value">{{ formatNumber(summary.sessionsStarted) }}</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">People who booked</div>
        <div class="trk-kpi-value">{{ formatNumber(summary.bookingsCompleted) }}</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Success rate</div>
        <div class="trk-kpi-value">{{ formatPct(summary.completionRate) }}</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Left without booking</div>
        <div class="trk-kpi-value">{{ formatNumber(summary.abandoned) }}</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Support contacts</div>
        <div class="trk-kpi-value">{{ formatNumber(supportTotal) }}</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Open / regressed issues</div>
        <div class="trk-kpi-value">{{ formatNumber(issueCount) }}</div>
      </div>
    </div>

    <b-row>
      <b-col md="6">
        <div class="trk-panel">
          <div class="trk-panel-head"><div><h2>Where people dropped off</h2><p>Focus improvements here first</p></div></div>
          <div class="trk-panel-body">
            <table class="table trk-table mb-0" v-trk-resizable="'weekly-dropoffs'">
              <thead><tr><th>Step</th><th>People</th><th>%</th></tr></thead>
              <tbody>
                <tr v-for="row in (summary.topDropOffs || [])" :key="row.step">
                  <td>{{ row.step }}</td>
                  <td>{{ formatNumber(row.dropOffs) }}</td>
                  <td>{{ formatPct(row.percent) }}</td>
                </tr>
                <tr v-if="!(summary.topDropOffs || []).length"><td colspan="3" class="text-muted">No drop-offs</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-col>
      <b-col md="6">
        <div class="trk-panel">
          <div class="trk-panel-head"><div><h2>Doctor slot advice</h2><p>When demand is higher than supply</p></div></div>
          <div class="trk-panel-body">
            <ul class="mb-0" v-if="recommendations.length">
              <li v-for="(item, idx) in recommendations" :key="idx">{{ item }}</li>
            </ul>
            <div class="trk-empty" v-else>
              <strong>No staffing pressure detected</strong>
              Demand looks covered by available slots in this period.
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import {
  buildInsight,
  defaultDateRange,
  formatNumber,
  formatPct,
  rangeParams
} from './trackingShared';

export default {
  name: 'TrackingWeeklyReport',
  components: { TrackingPageHeader },
  data() {
    const range = defaultDateRange(7);
    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
      report: {},
      loading: false
    };
  },
  computed: {
    chatFilters() {
      return rangeParams(this.fromDate, this.toDate);
    },
    pageSummary() {
      return `${this.formatNumber(this.summary.sessionsStarted)} started, ${this.formatNumber(this.summary.bookingsCompleted)} booked (${this.formatPct(this.summary.completionRate)})`;
    },
    summary() { return this.report.summary || {}; },
    insight() { return buildInsight(this.summary); },
    displayHighlights() {
      return (this.report.highlights && this.report.highlights.length)
        ? this.report.highlights
        : this.insight.lines;
    },
    recommendations() {
      return (this.report.capacity && this.report.capacity.recommendedWindows) || [];
    },
    supportTotal() {
      return (this.report.support && this.report.support.totalInteractions) || 0;
    },
    issueCount() {
      const fps = (this.report.issues && this.report.issues.fingerprints) || [];
      return fps.filter((f) => f.regressed || f.status !== 'RESOLVED').length;
    }
  },
  created() { this.loadData(); },
  methods: {
    formatNumber,
    formatPct,
    loadData() {
      this.loading = true;
      this.$onboardingTrackingService.getWeeklyReport(rangeParams(this.fromDate, this.toDate))
        .then((res) => { this.report = (res && res.data) || {}; })
        .finally(() => { this.loading = false; });
    },
    exportCsv() {
      this.$onboardingTrackingService.exportSessions(rangeParams(this.fromDate, this.toDate));
    }
  }
};
</script>
